import { HTMLAttributes, useState } from "react"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../common/alert-dialog"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import useTranslator from "@pma/hooks/use-translator"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { DictType } from "@pma/types"
import axios from "axios"
import { SERVER_URL, WORK_DIARY_RESTORE_ENDPOINT } from "@pma/utils/api-routes"
import { useAuth } from "@pma/stores/auth-store"
import { toast } from "@pma/hooks/use-toast"
import { Button } from "../common/button"

interface Props extends HTMLAttributes<HTMLDivElement> {
  workDiaries: DictType
}

const RestoreWorkDiary = ({ workDiaries, children, className }: Props) => {
  const { authConfig } = useAuth()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const queryClient = useQueryClient()

  const { translate } = useTranslator()
  const [open, setOpen] = useState(false)

  const restoreWorkDiaries = async () => {
    const response = await axios.post(
      SERVER_URL + WORK_DIARY_RESTORE_ENDPOINT,
      {
        organization_id: authConfig.last_organization_id,
        diaries_id: [workDiaries?.id],
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const restoreWorkDiariesMutation = useMutation({
    mutationKey: ["restore-work-diaries", workDiaries?.id],
    mutationFn: restoreWorkDiaries,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] })
      toast({
        title: translate("Success"),
        description: `${workDiaries?.number} ${translate("has been restored")}`,
        variant: "success",
      })
      setOpen(false)
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage ||
          `${translate("Unable to restored")} ${workDiaries?.number} ${translate("Work Diaries")}`,
        variant: "error",
      })
      setOpen(false)
    },
  })

  return (
    <AlertDialog open={open}>
      <AlertDialogTrigger className={className} onClick={() => setOpen(true)}>
        <Tooltip>
          <TooltipTrigger>{children}</TooltipTrigger>
          <TooltipContent className='flex items-center gap-1 bg-destructive'>
            <h4>
              {translate(`Restore`)} {workDiaries?.number}
            </h4>
          </TooltipContent>
        </Tooltip>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to restore")} {workDiaries?.number}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This will restore the work diaries")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setOpen(false)}>
            {translate("Cancel")}
          </AlertDialogCancel>
          <Button
            disabled={restoreWorkDiariesMutation?.isPending}
            loading={restoreWorkDiariesMutation?.isPending}
            onClick={() => restoreWorkDiariesMutation.mutate()}
          >
            {translate("Continue")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default RestoreWorkDiary
